import React from 'react'
import {
  Modal,
  Button,
  ModalInjectedProps,
  Image,
  Link,
  Banner,
} from '@sketch/components'
import {
  GetIntegrationConnectUrlMutationVariables,
  GetWorkspaceIntegrationsDocument,
  useGetIntegrationQuery,
  useRemoveIntegrationMutation,
} from '@sketch/gql-types'
import { dateFormat } from '@sketch/utils'
import { useThemeContext } from '@sketch/global-styles'

import { INTEGRATION_INFO } from './integrationInfo'
import { IntegrationSettingsModalSkeleton } from './IntegrationSettingsModalSkeleton'
import {
  Header,
  IconWrapper,
  HeaderContent,
  IntegrationTitle,
  Section,
  SectionTitle,
  SectionContent,
  InfoTable,
  InfoLabel,
  InfoValue,
  BlurredLogo,
  BlurredLogoContainer,
  StyledModal,
} from './IntegrationSettingsModal.styles'
import { SKETCH_WEBSITE } from '@sketch/env-config'
import { useToast } from '@sketch/toasts'

interface IntegrationSettingsModalProps extends ModalInjectedProps {
  integrationProvider: GetIntegrationConnectUrlMutationVariables['provider']
  workspaceId: string
  integrationName: string
}

export const IntegrationSettingsModal: React.FC<
  IntegrationSettingsModalProps
> = ({ hideModal, integrationProvider, workspaceId, integrationName }) => {
  const { showToast } = useToast()
  const { isDarkMode } = useThemeContext()

  const { data, loading, error } = useGetIntegrationQuery({
    variables: {
      workspaceIdentifier: workspaceId,
      integrationProvider,
    },
  })

  const integrationIdentifier = data?.integration?.identifier

  const [removeIntegration, { loading: removeLoading }] =
    useRemoveIntegrationMutation({
      variables: {
        identifier: integrationIdentifier!,
      },
      onCompleted: () => {
        showToast(
          `${integrationName} integration removed successfully`,
          'positive'
        )
        hideModal()
      },
      onError: () => {
        showToast('Something went wrong removing integration', 'negative')
      },
      refetchQueries: [
        {
          query: GetWorkspaceIntegrationsDocument,
          variables: {
            identifier: workspaceId,
          },
        },
      ],
      awaitRefetchQueries: true,
    })

  if (loading) {
    return <IntegrationSettingsModalSkeleton hideModal={hideModal} />
  }

  const handleUnlink = () => {
    removeIntegration()
  }

  const externalWorkspaceName = data?.integration?.externalWorkspaceName
  const addedBy = data?.integration?.managedBy.name
  const since = data?.integration?.createdAt

  const integrationInfo = INTEGRATION_INFO[integrationProvider]

  const formattedDate = since
    ? dateFormat(new Date(since), 'en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '-'

  return (
    <StyledModal onCancel={hideModal}>
      <Modal.Body>
        <BlurredLogoContainer $isDarkMode={isDarkMode}>
          <BlurredLogo
            alt={integrationInfo.name}
            src={integrationInfo.icon}
            srcSet={`${integrationInfo.icon2x} 2x`}
          />
        </BlurredLogoContainer>
        {/* Icon, Integration name and Website */}
        <Header>
          <IconWrapper>
            <Image
              alt={integrationInfo.name}
              src={integrationInfo.icon}
              srcSet={`${integrationInfo.icon2x} 2x`}
              width="64px"
              height="64px"
            />
          </IconWrapper>
          <HeaderContent>
            <IntegrationTitle>
              {integrationInfo.name} Integration
            </IntegrationTitle>
            <Link
              variant="tertiary"
              isUnderlined
              href={integrationInfo.website}
              external
            >
              Website ↗
            </Link>
          </HeaderContent>
        </Header>

        <Section>
          <SectionTitle>About {integrationInfo.name}</SectionTitle>
          <SectionContent>{integrationInfo.about}</SectionContent>
        </Section>

        {error ? (
          <Banner type="error" showIcon={false}>
            We ran into a problem accessing the data from this integration. If
            this error continues,{' '}
            <Link
              variant="secondary"
              href={`${SKETCH_WEBSITE}/support/`}
              external
              isUnderlined
            >
              contact our Support team
            </Link>
          </Banner>
        ) : (
          <>
            <Section>
              <SectionTitle>Information</SectionTitle>
              <InfoTable>
                <tbody>
                  <tr>
                    <InfoLabel>{integrationInfo.name} Workspace</InfoLabel>
                    <InfoValue>{externalWorkspaceName}</InfoValue>
                  </tr>

                  <tr>
                    <InfoLabel>Added by</InfoLabel>
                    <InfoValue>{addedBy}</InfoValue>
                  </tr>

                  <tr>
                    <InfoLabel>Since</InfoLabel>
                    <InfoValue>{formattedDate}</InfoValue>
                  </tr>
                </tbody>
              </InfoTable>
            </Section>
          </>
        )}
      </Modal.Body>
      <Modal.Footer
        leftContent={
          <Button
            variant="tertiary"
            onClick={handleUnlink}
            loading={removeLoading}
          >
            Unlink
          </Button>
        }
      >
        <Button variant="primary" onClick={hideModal}>
          Close
        </Button>
      </Modal.Footer>
    </StyledModal>
  )
}
